import React, { useState } from 'react';
import {
  Typography,
  Paper,
  Box,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Divider,
  Alert,
} from '@mui/material';
import { Visibility as ViewIcon, CheckCircle, Error as ErrorIcon, Info } from '@mui/icons-material';
import { useAppSelector } from '../../../../../app/hooks';
import {
  selectHasCalculationResult,
  selectCalculationResult,
  selectContractResolution,
  selectProcedureResults,
} from '../../../../../services/ContractService/contractEstimateSlice';
import { AuditTrailDialog } from '../../dialogs/estimateDialogs/AuditTrailDialog';

/**
 * Format currency values
 */
const formatCurrency = (value: number | undefined): string => {
  if (value === undefined || value === null) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
};

export const CalculationResultsSection: React.FC = () => {
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  
  const hasResult = useAppSelector(selectHasCalculationResult);
  const result = useAppSelector(selectCalculationResult);
  const contractResolution = useAppSelector(selectContractResolution);
  const procedureResults = useAppSelector(selectProcedureResults);

  if (!hasResult || !result) {
    return null;
  }

  const contractFound = contractResolution?.found;

  return (
    <>
      <Paper elevation={2} sx={{ p: 3, backgroundColor: '#f0fdf4', border: '1px solid #86efac' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#166534' }}>
            Calculation Results
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ViewIcon />}
            onClick={() => setAuditDialogOpen(true)}
            sx={{ textTransform: 'none' }}
          >
            View Audit Trail
          </Button>
        </Stack>

        {/* Contract Resolution */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151', fontWeight: 600 }}>
            Contract Resolution
          </Typography>
          {contractFound ? (
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                icon={<CheckCircle />}
                label="Contract Found"
                color="success"
                size="small"
              />
              {contractResolution?.payerName && (
                <Chip label={`Payer: ${contractResolution.payerName}`} size="small" variant="outlined" />
              )}
              {contractResolution?.product && (
                <Chip label={`Product: ${contractResolution.product}`} size="small" variant="outlined" />
              )}
              {contractResolution?.calcType && (
                <Chip label={`Type: ${contractResolution.calcType}`} size="small" variant="outlined" />
              )}
              {contractResolution?.financialClass && (
                <Chip label={`FC: ${contractResolution.financialClass}`} size="small" variant="outlined" />
              )}
            </Stack>
          ) : (
            <Alert severity="warning" icon={<ErrorIcon />}>
              No matching contract found. {contractResolution?.errorMessage || ''}
            </Alert>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Procedure Results Table */}
        {procedureResults.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#374151', fontWeight: 600 }}>
              Procedure Results
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#e2e8f0' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Base Rate</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Adjusted</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Implant</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Final</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {procedureResults.map((proc, index) => (
                    <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f1f5f9' } }}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{proc.code}</TableCell>
                      <TableCell>
                        <Chip label={proc.procedureType} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(proc.baseRate)}</TableCell>
                      <TableCell align="right">{formatCurrency(proc.modifierAdjustedRate || proc.discountedRate)}</TableCell>
                      <TableCell align="right">{formatCurrency(proc.implantAddOn)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCurrency(proc.finalAmount)}</TableCell>
                      <TableCell>
                        <Chip label={proc.rateSource || 'N/A'} size="small" color="info" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Totals */}
        <Stack direction="row" spacing={4} justifyContent="flex-end">
          <Box textAlign="right">
            <Typography variant="caption" color="text.secondary">Total Charges</Typography>
            <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
              {formatCurrency(result.totalBilledCharges)}
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography variant="caption" color="text.secondary">Total Reimbursement</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#166534', fontFamily: 'monospace' }}>
              {formatCurrency(result.totalReimbursement)}
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Audit Trail Dialog */}
      <AuditTrailDialog
        open={auditDialogOpen}
        onClose={() => setAuditDialogOpen(false)}
      />
    </>
  );
};
